<template>
  <div class="event-info-card" v-if="innerEvent">
    <div class="event-main">
      <div class="event-cover-wrapper" v-if="innerEvent?.coverurl">
        <img :src="innerEvent.coverurl" alt="活动封面" class="event-cover" />
      </div>

      <div class="event-detail">
        <h2 class="event-title">{{ eventTitle }}</h2>

        <div class="event-meta">
          <div class="meta-item">
            <svg class="meta-icon" viewBox="0 0 24 24" fill="none">
              <path d="M8 2V5M16 2V5M3.5 9.08984H20.5M21 8.5V17C21 18.933 19.433 20.5 17.5 20.5H6.5C4.567 20.5 3 18.933 3 17V8.5C3 6.567 4.567 5 6.5 5H17.5C19.433 5 21 6.567 21 8.5Z" stroke="#666" stroke-width="1.5" stroke-linecap="round"/>
            </svg>
            <span>{{ eventDateRange }}</span>
          </div>

          <div class="meta-item">
            <svg class="meta-icon" viewBox="0 0 24 24" fill="none">
              <path d="M12 13C14.6522 13 16.75 10.9022 16.75 8.25C16.75 5.59783 14.6522 3.5 12 3.5C9.34784 3.5 7.25 5.59783 7.25 8.25C7.25 10.9022 9.34784 13 12 13Z" stroke="#666" stroke-width="1.5"/>
              <path d="M12 20.5C14.25 16.75 19 13.4183 19 8.25C19 4.50317 15.866 1.5 12 1.5C8.13401 1.5 5 4.50317 5 8.25C5 13.4183 9.75 16.75 12 20.5Z" stroke="#666" stroke-width="1.5"/>
            </svg>
            <span>{{ eventAddress }}</span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { formatTimestampToDate } from '@/utils/dateUtil';
import { getEvent } from '@/api/event';
import config from '@/config';
import { eventBus } from '@/utils/eventBus.js';

export default {
  props: {
    event: {
      type: Object,
      default: () => ({})
    },
    ticket: {
      type: Object,
      default: () => ({})
    },
    status: {
      type: Number,
      default: undefined
    },
    orderpaystatus: {
      type: Number,
      default: undefined
    }
  },
  data() {
    return {
      innerEvent: null
    };
  },
  computed: {
    isEn() {
      const locale = this.$i18n?.locale || localStorage.getItem('locale') || 'zh-cn';
      return locale === 'en' || locale === 'en-US';
    },
    eventTitle() {
      if (this.isEn) {
        return '4th International Conference on Engineering Defect Mechanics';
      }
      return this.innerEvent?.fullname || '默认活动名称';
    },
    eventDateRange() {
      if (!this.innerEvent?.starttime || !this.innerEvent?.closetime) {
        return this.isEn ? 'Date to be announced' : '日期待定';
      }
      
      const start = this.innerEvent.starttime;
      const close = this.innerEvent.closetime;
      
      if (this.isEn) {
        // 英文格式：From October 16th, 2026 to October 18th, 2026
        const startDate = new Date(start * 1000);
        const endDate = new Date(close * 1000);
        
        const months = ['January', 'February', 'March', 'April', 'May', 'June', 
                        'July', 'August', 'September', 'October', 'November', 'December'];
        
        const getOrdinalSuffix = (day) => {
          if (day > 3 && day < 21) return 'th';
          switch (day % 10) {
            case 1: return 'st';
            case 2: return 'nd';
            case 3: return 'rd';
            default: return 'th';
          }
        };
        
        const startMonth = months[startDate.getMonth()];
        const startDay = startDate.getDate();
        const startYear = startDate.getFullYear();
        const startOrdinal = getOrdinalSuffix(startDay);
        
        const endMonth = months[endDate.getMonth()];
        const endDay = endDate.getDate();
        const endYear = endDate.getFullYear();
        const endOrdinal = getOrdinalSuffix(endDay);
        
        return `From ${startMonth} ${startDay}${startOrdinal}, ${startYear} to ${endMonth} ${endDay}${endOrdinal}, ${endYear}`;
      }
      
      // 中文格式
      return `${formatTimestampToDate(start)} ~ ${formatTimestampToDate(close)}`;
    },
    eventAddress() {
      if (this.isEn) {
        return 'Beijing Conference Center (No. 88 Laiguangying West Road, Chaoyang District, Beijing)';
      }
      return this.innerEvent?.address || '待定';
    },
    formattedSummary() {
      if (!this.innerEvent?.summary) return '';
      return this.innerEvent.summary.replaceAll('\n', '<br>');
    }
  },
  created() {
    this.fetchEventInfo();
  },
  methods: {
    formatTimestampToDate,
    async fetchEventInfo() {
      try {
        const res = await getEvent(config.EVENTID);
        if (res.code < 300) {
          this.innerEvent = res.data;
          // 🔥 发送 event-ready 事件
          this.$emit('event-ready', res.data);
          // 🔥 通过 eventBus 发送
          eventBus.$emit('event-ready', res.data);
        }
      } catch (err) {
        console.warn('获取活动信息失败', err);
      }
    }
  }
};
</script>

<style scoped>
.event-info-card {
  background: #ffffff;
  border-radius: 12px;
  padding: 24px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
}

.event-main {
  display: flex;
  gap: 20px;
  align-items: flex-start;
}

.event-cover-wrapper {
  flex-shrink: 0;
  width: auto;
  height: 157px;
  border-radius: 8px;
  overflow: hidden;
  background: #f5f5f5;
}

.event-cover {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.event-detail {
  flex: 1;
  min-width: 0;
  padding-top: 4px;
}

.event-title {
  margin: 0 0 12px;
  font-size: 24px;
  font-weight: 700;
  color: #111;
  line-height: 1.4;
}

.event-meta {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.meta-item {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  color: #666;
}

.meta-icon {
  width: 16px;
  height: 16px;
  flex-shrink: 0;
}

.event-summary {
  margin-top: 24px;
  padding-top: 24px;
  border-top: 1px dashed #ddd;
}

.summary-label {
  margin-bottom: 12px;
  font-size: 16px;
  font-weight: 700;
  color: #222;
}

.summary-text {
  font-size: 14px;
  color: #444;
  line-height: 1.8;
}

.summary-text :global(p) {
  margin: 0 0 10px;
}
.summary-text :global(br) {
  line-height: 1.8;
}
.summary-text :global(ul) {
  margin: 0;
  padding-left: 20px;
}
.summary-text :global(li) {
  margin-bottom: 8px;
}

@media (max-width: 767px) {
  .event-main {
    flex-direction: column;
  }
  .event-cover-wrapper {
    width: 100%;
    height: 180px;
  }
  .event-title {
    font-size: 20px;
  }
}
</style>